import { create } from 'zustand'
import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  orderBy,
  limit as firestoreLimit,
  Timestamp,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '../config/firebase'
import { User } from '../models/User'

export interface ScreenLog {
  screenName: string
  duration: number
  enteredAt: Date
  exitedAt: Date
  sessionId: string
}

export interface UsageStats {
  lastSessionStart?: Date
  lastSessionEnd?: Date
  totalSessions: number
  screenTimeByPage: Record<string, number>
}

interface UserState {
  users: User[]
  selectedUser: User | null
  screenLogs: ScreenLog[]
  usageStats: UsageStats | null
  loading: boolean
  deleting: boolean
  creating: boolean
  error: string | null
  fetchUsers: () => Promise<void>
  fetchUser: (userId: string) => Promise<void>
  fetchUserScreenLogs: (userId: string, count?: number) => Promise<void>
  fetchUserUsageStats: (userId: string) => Promise<void>
  deleteUser: (userId: string) => Promise<void>
  createUser: (email: string, password: string, displayName?: string) => Promise<void>
  clearSelectedUser: () => void
}

export const useUserStore = create<UserState>((set, get) => ({
  users: [],
  selectedUser: null,
  screenLogs: [],
  usageStats: null,
  loading: false,
  deleting: false,
  creating: false,
  error: null,

  fetchUsers: async () => {
    set({ loading: true, error: null })
    try {
      const q = query(collection(db, 'users'), orderBy('registeredAt', 'desc'))
      const snapshot = await getDocs(q)

      const users: User[] = snapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          email: data.email,
          displayName: data.displayName,
          gender: data.gender,
          birthDate: (data.birthDate as Timestamp).toDate(),
          education: data.education,
          grade: data.grade,
          courses: data.courses || [],
          registeredAt: (data.registeredAt as Timestamp).toDate(),
          registeredBy: data.registeredBy,
          loginCount: data.loginCount || 0,
          lastLoginAt: data.lastLoginAt
            ? (data.lastLoginAt as Timestamp).toDate()
            : undefined,
          lastSeen: data.lastSeen
            ? (data.lastSeen as Timestamp).toDate()
            : undefined,
          fcmToken: data.fcmToken,
          totalScreenTime: data.totalScreenTime || 0,
        }
      })

      set({ users, loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '載入用戶失敗',
      })
    }
  },

  fetchUser: async (userId: string) => {
    set({ loading: true, error: null })
    try {
      const docRef = doc(db, 'users', userId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        throw new Error('找不到此用戶')
      }

      const data = docSnap.data()
      const user: User = {
        id: docSnap.id,
        email: data.email,
        displayName: data.displayName,
        gender: data.gender,
        birthDate: (data.birthDate as Timestamp).toDate(),
        education: data.education,
        grade: data.grade,
        courses: data.courses || [],
        registeredAt: (data.registeredAt as Timestamp).toDate(),
        registeredBy: data.registeredBy,
        loginCount: data.loginCount || 0,
        lastLoginAt: data.lastLoginAt
          ? (data.lastLoginAt as Timestamp).toDate()
          : undefined,
        lastSeen: data.lastSeen
          ? (data.lastSeen as Timestamp).toDate()
          : undefined,
        fcmToken: data.fcmToken,
        totalScreenTime: data.totalScreenTime || 0,
      }

      set({ selectedUser: user, loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '載入用戶失敗',
      })
    }
  },

  fetchUserScreenLogs: async (userId: string, count: number = 50) => {
    try {
      const q = query(
        collection(db, 'users', userId, 'screenLogs'),
        orderBy('exitedAt', 'desc'),
        firestoreLimit(count)
      )
      const snapshot = await getDocs(q)

      const logs: ScreenLog[] = snapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          screenName: data.screenName,
          duration: data.duration,
          enteredAt: (data.enteredAt as Timestamp).toDate(),
          exitedAt: (data.exitedAt as Timestamp).toDate(),
          sessionId: data.sessionId,
        }
      })

      set({ screenLogs: logs })
    } catch (error) {
      console.error('載入 screen logs 失敗:', error)
      set({ screenLogs: [] })
    }
  },

  fetchUserUsageStats: async (userId: string) => {
    try {
      const docRef = doc(db, 'users', userId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        set({ usageStats: null })
        return
      }

      const data = docSnap.data()
      const stats = data.usageStats

      if (!stats) {
        set({ usageStats: null })
        return
      }

      set({
        usageStats: {
          lastSessionStart: stats.lastSessionStart
            ? (stats.lastSessionStart as Timestamp).toDate()
            : undefined,
          lastSessionEnd: stats.lastSessionEnd
            ? (stats.lastSessionEnd as Timestamp).toDate()
            : undefined,
          totalSessions: stats.totalSessions || 0,
          screenTimeByPage: stats.screenTimeByPage || {},
        },
      })
    } catch (error) {
      console.error('載入 usage stats 失敗:', error)
      set({ usageStats: null })
    }
  },

  deleteUser: async (userId: string) => {
    set({ deleting: true, error: null })
    try {
      const deleteUserFn = httpsCallable<
        { userId: string },
        { success: boolean; message: string }
      >(functions, 'deleteUser')
      await deleteUserFn({ userId })
      // 刷新列表
      await get().fetchUsers()
      set({ deleting: false })
    } catch (error: any) {
      const message =
        error?.message || (error instanceof Error ? error.message : '刪除用戶失敗')
      set({
        deleting: false,
        error: message,
      })
      throw error
    }
  },

  createUser: async (email: string, password: string, displayName?: string) => {
    set({ creating: true, error: null })
    try {
      const createUserFn = httpsCallable<
        { email: string; password: string; displayName?: string },
        { success: boolean; uid: string }
      >(functions, 'createUser')
      await createUserFn({ email, password, displayName })
      await get().fetchUsers()
      set({ creating: false })
    } catch (error: any) {
      const message = error?.message || '建立用戶失敗'
      set({ creating: false, error: message })
      throw error
    }
  },

  clearSelectedUser: () => {
    set({ selectedUser: null, screenLogs: [], usageStats: null })
  },
}))
